import { Injectable } from '@angular/core';
import { SupportingDocument, DocumentCategory } from 'app/shared/model/supporting-document';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { LogService } from './log.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { DeathNotification } from 'app/notify/model/death-notification';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseApp } from '@angular/fire/app';

@Injectable()
export class DocumentsService {

  documents: SupportingDocument[] = [];
  private regionalFns;

  constructor(
    private http: HttpClient,
    private fns: AngularFireFunctions,
    private app: FirebaseApp,
    private logService: LogService
  ) {
    this.regionalFns = getFunctions(this.app, 'australia-southeast1');
  }

  public b64toBlob(b64Data: string, contentType: string = '', sliceSize: number = 512): Blob {
    // Handle both raw base64 and data URL formats
    const base64Data = b64Data.includes('base64,') ? b64Data.split('base64,')[1] : b64Data;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  /**
   * Opens a blob in a new browser tab, returning false if the browser blocked it.
   * The object URL is held until that tab is closed rather than revoked on a timer -
   * the browser's PDF viewer re-fetches the URL when the user downloads or prints,
   * so an early revoke makes those fail with a misleading network error.
   */
  public openBlobInNewTab(blob: Blob): boolean {
    const blobUrl = URL.createObjectURL(blob);
    this.logService.log(`Opening blob ${blobUrl}`);
    const openedWindow = window.open(blobUrl, '_blank');
    if (!openedWindow || openedWindow.closed || typeof openedWindow.closed === 'undefined') {
      URL.revokeObjectURL(blobUrl);
      return false;
    }
    const timer = setInterval(() => {
      if (openedWindow.closed) {
        clearInterval(timer);
        URL.revokeObjectURL(blobUrl);
      }
    }, 2000);
    return true;
  }

  public categoryDescription(category: DocumentCategory, suffix?: string, prefix?: string): string {
    switch (category) {
      case 'contractOfService':
        return `${prefix || ''}Letter of Engagement${suffix || ''}`;
      case 'probate':
        return `${prefix || ''}Probate or Letters of Administration${suffix || ''}`;
      case 'will':
        return `${prefix || ''}Will${suffix || ''}`;
      case 'statutoryDeclaration':
        return `${prefix || ''}Statutory declaration${suffix || ''}`;
      case 'otherDocument':
        return `${prefix || ''}Other document${suffix || ''}`;
      case 'advancedCarePlan':
        return `${prefix || ''}Advanced care plan${suffix || ''}`;
      case 'birthCertificate':
        return `${prefix || ''}Birth Certificate${suffix || ''}`;
      case 'marriageCertificate':
        return `${prefix || ''}Marriage or Civil Union Certificate${suffix || ''}`;
      case 'dissolutionOfMarriage':
        return `${prefix || ''}Dissolution of Marriage${suffix || ''}`;
      case 'contractingOutAgreement':
        return `${prefix || ''}Contracting Out Agreement${suffix || ''}`;
      case 'trustDeed':
        return `${prefix || ''}Trust Deed${suffix || ''}`;
      case 'powerOfAttorney':
        return `${prefix || ''}Enduring Power of Attorney (Property)${suffix || ''}`;
      case 'powerOfAttorneyPersonalCare':
        return `${prefix || ''}Power of Attorney (Personal Care and Welfare)${suffix || ''}`;
    }
  }

  public clearCache() {
    this.documents = [];
  }
  
  public getLetterPDF(notification: DeathNotification, letterType: 'engagement'): Promise<string> {
    const callable = httpsCallable(this.regionalFns, 'getLetterPDF');
    return callable({ notification: notification, letterType: letterType }).then(res => res.data as string);
  }

  // Take the array of documents and return a new array with any documents containing data uploaded and the data replaced by the download url
  public async saveDocuments(supports: 'plan' | 'notification', submissionToken?: string): Promise<SupportingDocument[]> {
    const promises = this.documents.map(async doc => {
      if (doc.data) {
        const result = await this.saveDocument(doc, supports, submissionToken);
        this.logService.log(`Save document was successful with result ${JSON.stringify(result, null, 4)}`);
        const savedDocument: SupportingDocument = {
          category: doc.category,
          status: 'attached',
          name: doc.name || this.categoryDescription(doc.category),
          // eslint-disable-next-line @typescript-eslint/dot-notation
          url: result['downloadUrl'] as string,
          physicalLocation: doc.physicalLocation,
        };
        return savedDocument;
      } else {
        return doc;
      }
    });
    const documents = await Promise.all(promises);
    return documents;
  }
  
  private saveDocument(document: SupportingDocument, supports: 'plan' | 'notification', submissionToken?: string): Promise<object> {
    if (submissionToken) {
      const url = environment.functionsURL + `/document`;
      this.logService.log('Calling ' + url);
      return this.http.post(url, {
        document: document,
        supports: supports,
        submissionToken: submissionToken,
      }).toPromise();
    } else {
      const callable = httpsCallable(this.regionalFns, 'saveDocument');
      return callable({
        document: document,
      }).then(res => res.data as object);
    }
  }
}
